import { useResource, Resources } from "Translations/Resources";
import { Box, Stack, Typography, Divider } from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { NotificationTypeSelect } from "Components/Shared/Inputs/Form/NotificationTypeSelect";
import { DateFormInput } from "Components/Shared/Inputs/Form/DateFormInput";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import {
  NotificationStatus,
  NotificationVariant,
} from "Infrastructure/Api/Api";
import { TimeFormInput } from "Components/Shared/Inputs/Form/TimeFormInput";
import { CodeListAutocomplete } from "Components/Shared/Inputs/Form/CodeListAutocomplete";
import { mergeDateTimeToUTC } from "Utils/DateUtils";
import { useCreateNotificationMutation } from "Api/Mutations/Notifications/useCreateNotificationMutation";
import { useUpdateNotificationMutation } from "Api/Mutations/Notifications/useUpdateNotificationMutation";
import type { NotificationDto } from "Infrastructure/Api/Api";
import {
  createNotificationSchema,
  type NotificationFormData,
} from "Components/Notifications/NotificationsManagement/NotificationForm/schema";
import { appUrl } from "Utils/UrlUtils";
import { useGoBack } from "Navigation/useGoBack";
import {
  getBankOptions,
  getDefaultValues,
  getUserTypeOptions,
} from "Components/Notifications/NotificationsManagement/NotificationForm/utils";
import { Notification } from "Components/Shared/Notification/Notification";
import { useMemo } from "react";
import { formatRelativeTime } from "Utils/TimeUtils";

const RESOURCES = Resources.Pages.Content.NotificationForm;

type Props =
  | {
      mode?: "create";
      notificationData?: undefined;
    }
  | {
      mode: "update";
      notificationData: NotificationDto;
    };

export const NotificationForm = ({
  notificationData,
  mode = "create",
}: Props) => {
  const { navigateBack } = useGoBack({
    mode: "historyStack",
    fallbackUrl: appUrl("notifications-management"),
  });
  const { t } = useResource();
  const { mutate: createNotification, isPending: isPendingCreate } =
    useCreateNotificationMutation({
      onSuccess: navigateBack,
    });
  const { mutate: updateNotification, isPending: isPendingUpdate } =
    useUpdateNotificationMutation({
      onSuccess: navigateBack,
    });

  const isEditMode = mode === "update";
  const isDispatched =
    notificationData?.status === NotificationStatus.Dispatched;

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = useForm<NotificationFormData>({
    resolver: zodResolver(
      createNotificationSchema(t, { skipDateTimeValidation: isDispatched }),
    ),
    defaultValues: getDefaultValues(isEditMode, notificationData),
  });

  const durationType = watch("durationType");
  const formValues = watch();

  // Generate preview data from current form values
  const previewData = useMemo(() => {
    const title = formValues.title || t(RESOURCES.Name);
    const content = formValues.content;
    const variant = formValues.variant || NotificationVariant.Announcement;

    return {
      name: title,
      content: content,
      variant: variant,
      time: formatRelativeTime(new Date(), t),
    };
  }, [formValues.title, formValues.content, formValues.variant, t]);

  const onSubmit = ({
    duration: { from, to },
    title,
    content,
    variant,
    userTypes,
    banks,
    link,
  }: NotificationFormData) => {
    const scheduledDateTime = mergeDateTimeToUTC(from.date, from.time);
    const expirationDateTime =
      durationType === "period" && to?.date && to?.time
        ? mergeDateTimeToUTC(to.date, to.time)
        : null;

    const notificationPayload = {
      title,
      content,
      variant,
      scheduledDate: scheduledDateTime,
      expirationDate: expirationDateTime,
      userTypes: userTypes && userTypes.length > 0 ? userTypes : undefined,
      banks: banks && banks.length > 0 ? banks : undefined,
      link: link && link.trim() !== "" ? link : null,
    };

    if (isEditMode && notificationData) {
      updateNotification({
        publicID: notificationData.publicID,
        data: notificationPayload,
      });
    } else {
      createNotification(notificationPayload);
    }
  };

  return (
    <PrimaryPaper>
      <Stack component="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack>
          <FormInput
            name="title"
            control={control}
            errors={errors}
            label={t(RESOURCES.Name)}
          />
          <FormInput
            name="content"
            control={control}
            errors={errors}
            label={t(RESOURCES.Description)}
            multiline
            rows={4}
          />
          <NotificationTypeSelect
            name="variant"
            control={control}
            label={t(RESOURCES.NotificationType)}
          />
          <CodeListAutocomplete
            disabled //TODO (out of scope): ZS-843
            name="userTypes"
            label={t(RESOURCES.Filters.UserTypes)}
            control={control}
            options={getUserTypeOptions(t)}
            placeholder={t(RESOURCES.Filters.UserTypesPlaceholder)}
          />
          <CodeListAutocomplete
            disabled //TODO (out of scope): ZS-843
            name="banks"
            label={t(RESOURCES.Filters.Banks)}
            control={control}
            options={getBankOptions(t)}
            placeholder={t(RESOURCES.Filters.BanksPlaceholder)}
          />
          <FormInput
            disabled //TODO (out of scope): ZS-843
            name="link"
            control={control}
            errors={errors}
            label={t(RESOURCES.Link)}
            type="url"
          />
          <Box
            display="flex"
            justifyContent="space-between"
            width="100%"
            columnGap={2}
            flexWrap="wrap"
          >
            <Box display="flex" flexBasis={["100%", "50%"]}>
              <DateFormInput
                name="duration.from.date"
                control={control}
                label={t(RESOURCES.Duration.FromDate)}
                disabled={isDispatched}
              />
            </Box>
            <TimeFormInput
              control={control}
              name="duration.from.time"
              label={t(RESOURCES.Duration.FromTime)}
              disabled={isDispatched}
            />
          </Box>
          {/* Experimental: Duration type selection, might not be implemented, ZS-843 */}
          {/* <Stack gap={2}>
            <Typography fontWeight={400}>
              {t(RESOURCES.DurationPrompt)}
            </Typography>
            <RadioButtons
              isErrorHidden={durationType === "period"}
              size="small"
              control={control}
              name="durationType"
              codeList={getDurationTypeOptions(t)}
              disabled={isDispatched}
            />
          </Stack>
          {durationType === "period" && (
            <Box
              display="flex"
              justifyContent="space-between"
              width="100%"
              columnGap={2}
              flexWrap="wrap"
            >
              <Box display="flex" flexBasis={["100%", "50%"]}>
                <DateFormInput
                  name="duration.to.date"
                  control={control}
                  label={t(RESOURCES.Duration.ToDate)}
                  disabled={isDispatched}
                />
              </Box>
              <TimeFormInput
                control={control}
                name="duration.to.time"
                label={t(RESOURCES.Duration.ToTime)}
                disabled={isDispatched}
              />
            </Box>
          )} */}
          <Box my={3}>
            <Divider sx={{ mb: 7 }} />
            <Typography variant="h6" fontWeight={600} mb={2}>
              {t(RESOURCES.Preview)}
            </Typography>
            <Notification
              name={previewData.name}
              content={previewData.content}
              variant={previewData.variant}
              time={previewData.time}
              withPaper
            />
          </Box>
          <PrimaryButton
            fullWidth
            type="submit"
            disabled={
              !isDirty || (isEditMode ? isPendingUpdate : isPendingCreate)
            }
            isLoading={isEditMode ? isPendingUpdate : isPendingCreate}
            variant="contained"
            color="primary"
          >
            {isEditMode ? t(Resources.Common.Edit) : t(RESOURCES.Create)}
          </PrimaryButton>
        </Stack>
      </Stack>
    </PrimaryPaper>
  );
};
